import { FC } from 'react';

import { Badge, Button, ContentBlockProps, CtaLink, Grid, HeadingTagType } from '@purpur/library';
import { Icon } from '@purpur/library/icon';
import { arrowRight } from '@purpur/library/icon/assets/arrow-right';

import { NamedIcon } from '@telialt/common-icon/src';
import { RichTextContent } from '@telialt/common-rich-text-content/src/RichTextContent';

import { ImageLink } from '../common/ImageLink';
import { CtaProvider } from '../CtaProvider/CtaProvider';
import {
    ContentCardBadgeWrapper,
    ContentCardContainer,
    ImageCtasContainer,
    StyledContentBlock,
    StyledResponsiveImage,
    Video,
} from './BlockContent.styles';
import { ContentCardData } from './model';
import { CONTENT_CARD_TEST_IDS } from './test-ids';
import { VideoIframe } from './VideoIframe';

type Props = {
    card: ContentCardData;
    headingTag: HeadingTagType;
};

export const ContentCard: FC<Props> = ({ card, headingTag }) => {
    const {
        alignment,
        backgroundColor,
        isNegative,
        title,
        badge,
        badgeIcon,
        description,
        descriptionList,
        media,
        ctas,
    } = card;

    const badgeElement = badge && (
        <ContentCardBadgeWrapper>
            {badgeIcon ? (
                <Badge variant="special" showIcon={false} data-test={CONTENT_CARD_TEST_IDS.BADGE}>
                    <NamedIcon name={badgeIcon} size="xs" />
                    {badge}
                </Badge>
            ) : (
                <Badge variant="special" data-test={CONTENT_CARD_TEST_IDS.BADGE}>
                    {badge}
                </Badge>
            )}
        </ContentCardBadgeWrapper>
    );

    const image = media ? <StyledResponsiveImage images={media} /> : undefined;
    const youtubeVideo = card.youtubeVideo && (
        <VideoIframe src={card.youtubeVideo} title={`${card.title || ''} video`} />
    );
    const video = card.video && <Video src={card.video.src} title={card.video.title} data-element="Video" />;

    const actions: ContentBlockProps['actions'] = (
        <>
            {ctas
                ?.filter((cta) => !cta.image)
                .map((cta, index) => (
                    <CtaProvider cta={cta} key={index}>
                        {({ href, target, rel, text, onClick }) =>
                            href ? (
                                <CtaLink
                                    variant={index === 0 ? 'primary' : 'secondary'}
                                    href={href}
                                    target={target}
                                    rel={rel}
                                    onClick={onClick}
                                    negative={isNegative}
                                    data-element="Button"
                                    data-element-name={text}
                                    data-element-type={index === 0 ? 'Primary' : 'Secondary'}
                                >
                                    {text}
                                </CtaLink>
                            ) : (
                                <Button
                                    variant={index === 0 ? 'primary' : 'secondary'}
                                    onClick={onClick}
                                    negative={isNegative}
                                    data-element="Button"
                                    data-element-name={text}
                                    data-element-type={index === 0 ? 'Primary' : 'Secondary'}
                                >
                                    {text}
                                    <Icon svg={arrowRight} size="xs" />
                                </Button>
                            )
                        }
                    </CtaProvider>
                ))}
        </>
    );

    const imageCtas = ctas?.filter((cta) => cta.image);

    return (
        <ContentCardContainer
            $backgroundVariant={backgroundColor}
            data-section="Content Card"
            data-section-name={title}
            data-section-display-option={alignment || 'left'}
            data-section-color-option={backgroundColor}
        >
            <Grid noGap={true}>
                <StyledContentBlock
                    $alignment={alignment}
                    negative={isNegative}
                    title={title}
                    headingTag={headingTag}
                    headingVariant="title-300"
                    beforeTitle={badgeElement}
                    listItems={descriptionList}
                    image={image}
                    actions={actions}
                    video={video || youtubeVideo || undefined}
                >
                    <RichTextContent richText={description} isNegative={isNegative} />
                    {imageCtas?.length > 0 && (
                        <ImageCtasContainer>
                            {imageCtas.map((cta, index) => (
                                <ImageLink key={index} cta={cta} />
                            ))}
                        </ImageCtasContainer>
                    )}
                </StyledContentBlock>
            </Grid>
        </ContentCardContainer>
    );
};
